// AYA PRO — Activation du compte par lien sécurisé (maquette Réf 3 : « Votre compte est activé ! »).
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Phone, Mail, CheckCircle2, ChevronRight, ShieldCheck, Store, Package, Users, BarChart3 } from "lucide-react";
import { AyaProLogo } from "@/components/pro/AyaProLogo";
import "./ayapro.css";

const API = process.env.REACT_APP_BACKEND_URL;

const ACTIONS = [
  { Icon: Store, bg: "#E8F8EF", c: "#0E8A4F", t: "Compléter ma fiche", s: "Ajoutez vos photos, vos services et votre description." },
  { Icon: Package, bg: "#EAF2FB", c: "#2563EB", t: "Gérer mes produits ou services", s: "Publiez vos offres et tarifs." },
  { Icon: Users, bg: "#FDF3E3", c: "#F0590B", t: "Recevoir des clients", s: "Soyez visible sur AYA et recevez des demandes." },
  { Icon: BarChart3, bg: "#FBEAF0", c: "#DB2777", t: "Suivre mon activité", s: "Consultez vos statistiques en temps réel." },
];

export default function AyaProActivatePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "loading", account: null, error: null });
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    fetch(`${API}/api/ayapro/activate`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token }) })
      .then((r) => r.json())
      .then((j) => {
        if (j.ok) setState({ status: "ok", account: j.account, error: null });
        else setState({ status: "error", account: null, error: j.error });
      })
      .catch(() => setState({ status: "error", account: null, error: "network" }));
  }, [token]);

  const errMsg = { token_expired: "Ce lien a expiré (validité 24h). Recommencez l'inscription pour recevoir un nouveau lien.", token_already_used: "Ce lien a déjà été utilisé. Votre compte est probablement déjà actif — connectez-vous.", invalid_token: "Ce lien d'activation est invalide.", network: "Erreur réseau — réessayez." }[state.error] || "Activation impossible.";

  return (
    <div className="ap-page" data-testid="ayapro-activate-page">
      <div className="ap-wrap">
        <div className="ap-head">
          <button className="ap-back" onClick={() => navigate("/pro/bienvenue")} data-testid="ayapro-activate-back"><ArrowLeft size={15} /> Retour</button>
          <AyaProLogo variant="onboarding" onLight />
          <span className="ap-lang">FR ▾</span>
        </div>

        {state.status === "loading" && (
          <div style={{ textAlign: "center", paddingTop: 70 }} data-testid="ayapro-activate-loading">
            <div style={{ fontSize: 30 }}>⏳</div>
            <p className="ap-sub">Activation de votre compte en cours…</p>
          </div>
        )}

        {state.status === "error" && (
          <div style={{ textAlign: "center", paddingTop: 40 }} data-testid="ayapro-activate-error">
            <span style={{ width: 72, height: 72, borderRadius: "50%", background: "#FEE2E2", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 30 }}>✕</span>
            <h1 className="ap-h1">Lien non valide</h1>
            <p className="ap-sub">{errMsg}</p>
            {state.error === "token_already_used"
              ? <button className="ap-cta" style={{ maxWidth: 360 }} onClick={() => navigate("/pro/connexion")} data-testid="ayapro-activate-err-login">Se connecter <ChevronRight size={16} /></button>
              : <button className="ap-cta" style={{ maxWidth: 360 }} onClick={() => navigate("/pro/inscription")} data-testid="ayapro-activate-err-signup">Reprendre l'inscription <ChevronRight size={16} /></button>}
          </div>
        )}

        {state.status === "ok" && state.account && (
          <div data-testid="ayapro-activate-success">
            <div style={{ position: "relative" }}>
              <img src="/aya/ayapro/merchant-woman.jpg" alt="" className="ap-herophoto" style={{ maxHeight: 160 }} />
              <span style={{ position: "absolute", right: 14, bottom: -16, width: 56, height: 56, borderRadius: "50%", background: "#0E8A4F", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", border: "4px solid #F6F8F5", boxShadow: "0 6px 16px rgba(14,138,79,0.35)" }}><CheckCircle2 size={26} /></span>
            </div>
            <h1 className="ap-h1" style={{ textAlign: "left", fontSize: 25, marginTop: 20 }}>Votre compte est activé !</h1>
            <p className="ap-sub" style={{ textAlign: "left", margin: "6px 0 0" }}>Bravo ! Vos identifiants ont été enregistrés avec succès. Vous pouvez désormais accéder à votre espace AYA PRO et commencer à gérer votre activité.</p>

            <div className="ap-card" style={{ background: "#F0FBF4", border: "1px solid #D7EEDF" }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 900 }}>Vos informations de connexion</h3>
              <p style={{ margin: "3px 0 0", fontSize: 11.5, color: "#6B7280" }}>Conservez bien ces informations.</p>
              <div className="ap-field" style={{ marginTop: 11, background: "#fff" }}>
                <Phone size={15} color="#6B7280" />
                <span className="ap-flag"><span style={{ background: "#F77F00" }} /><span style={{ background: "#fff" }} /><span style={{ background: "#009E60" }} /></span>
                <input value={state.account.phone} readOnly data-testid="ayapro-activate-phone" style={{ fontWeight: 800 }} />
                <span className="ap-badge"><CheckCircle2 size={13} /> Vérifié</span>
              </div>
              {state.account.email && (
                <div className="ap-field" style={{ marginTop: 9, background: "#fff" }}>
                  <Mail size={15} color="#6B7280" />
                  <input value={state.account.email} readOnly data-testid="ayapro-activate-email" />
                  <span className="ap-badge"><CheckCircle2 size={13} /> Enregistré</span>
                </div>
              )}
            </div>

            <h3 style={{ margin: "20px 0 0", fontSize: 17, fontWeight: 900 }}>Que pouvez-vous faire maintenant ?</h3>
            <div className="ap-grid2">
              {ACTIONS.map(({ Icon, bg, c, t, s }) => (
                <div key={t} className="ap-action" style={{ background: bg }}>
                  <span className="ic"><Icon size={19} color={c} /></span>
                  <div><h4>{t} <ChevronRight size={12} /></h4><p>{s}</p></div>
                </div>
              ))}
            </div>

            <button className="ap-cta" onClick={() => navigate("/pro/connexion")} data-testid="ayapro-activate-go-login">Accéder à mon espace AYA PRO <ChevronRight size={17} /></button>

            <div className="ap-info" style={{ background: "#EFF8F1" }}>
              <span className="ic" style={{ background: "#0E8A4F" }}><ShieldCheck size={17} /></span>
              <div><b>Votre espace est sécurisé.</b> Vous pouvez modifier vos informations et votre mot de passe à tout moment dans les paramètres de votre compte.</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
